import { type JSX, useCallback, useState } from 'react';
import { css } from '@emotion/css';
import { ColorHex } from '../../ui/Colors.ts';
import { CurrentOperatorLoginId } from './CurrentOperatorLogin.tsx';
import { CurrentOperatorLoginDropdown } from './CurrentOperatorLoginDropdown.tsx';
import { SystemStatus } from '../../common/components/SystemStatusLink.tsx';
import { languageFromString } from 'dg-web-shared/lib/Localized.ts';
import {
    NavbarDropdown,
    NavbarDropdownApplicationItem,
    NavbarDropdownBody,
    NavbarDropdownHeader,
    NavbarDropdownItemForeignLink,
    NavbarDropdownVariant,
} from './NavbarDropdown.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorAppRoutes } from '../../app/config/OperatorRoutingConfig.tsx';
import { RestrictedComponent } from '../../app/components/RestrictedComponent.tsx';
import { NavLink, useNavigate } from 'react-router-dom';
import { BreadCrumb, getNavigationText } from './BreadCrumb.tsx';
import * as MasterDataZonesState from '../../common/state/MasterDataZonesState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import * as TariffState from '../../tariffs/state/TariffState.ts';
import { DateTime } from 'luxon';
import {
    isRouteAllowed,
    OperatorRouteType,
} from '../../app/components/OperatorRoutes.tsx';
import { Box, IconButton } from '@mui/material';
import { TermOfUseLink } from '../../landing-page/TermOfUseFiles.tsx';
import { Edit } from '@mui/icons-material';
import { Typo } from 'dg-web-shared/ui/typo.ts';

export function Navbar({
    zones,
    permitTypes,
    tariffs,
}: {
    zones: MasterDataZonesState.State;
    permitTypes: PermitTypeState.State;
    tariffs: TariffState.List.State;
}) {
    const { operatorData, settings } = useOperatorContext();

    const [show, setShow] = useState(false);

    return (
        <div
            className={css({
                position: 'absolute',
                top: 0,
                width: '100%',
                height: 48,
                background:
                    window.location.hostname !== 'parkingportal.ch'
                        ? ColorHex.fPendent
                        : ColorHex.navbar,
                color: '#FFFFFF',
            })}
        >
            <div
                className={css({
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                })}
            >
                <NavbarDropdown onClose={() => setShow(false)}>
                    <NavbarDropdownHeader
                        open={show}
                        hasDropDownIcon={false}
                        onClick={() => setShow(!show)}
                    >
                        <BreadCrumb />
                    </NavbarDropdownHeader>
                    <NavbarDropdownBody
                        open={show}
                        variant={NavbarDropdownVariant.Left}
                    >
                        <NavbarBody
                            onClose={() => setShow(false)}
                            zones={zones}
                            permitTypes={permitTypes}
                            tariffs={tariffs}
                        />
                        <div
                            className={css({
                                height: 48,
                                padding: '16px 32px 12px 12px',
                                color: '#fff',
                                background: '#32496B',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                fontSize: '13px',
                                fontWeight: 500,
                            })}
                        >
                            <Localized
                                de="Hilfe"
                                fr="Aide"
                                it="Aiuto"
                                en="Help"
                            />
                            &#58; &nbsp;
                            <a
                                className={css({ color: '#fff' })}
                                href="mailto:info@parkingportal.ch"
                            >
                                info@parkingportal.ch
                            </a>
                        </div>
                    </NavbarDropdownBody>
                </NavbarDropdown>

                <div
                    className={css({
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        paddingRight: 8,
                        alignItems: 'center',
                    })}
                >
                    {operatorData.organizationLoginHasMultipleLogins && (
                        <ChangeOperatorLogin />
                    )}
                    <CurrentOperatorLoginId />
                    <CurrentOperatorLoginDropdown />
                    <SystemStatus />
                    <TermOfUseLink
                        lang={languageFromString(settings.language)}
                        hasContractVersionAVBV2={
                            operatorData.hasContractVersionAVBV2
                        }
                    />
                </div>
            </div>
        </div>
    );
}

const navLinkClass = css({
    padding: '12px 14px',
    color: '#fff',
    display: 'block',
});

const activeNavLinkClass = css({
    color: '#4f9adb',
});

function NavbarBody({
    onClose,
    zones,
    permitTypes,
    tariffs,
}: {
    onClose: () => void;
    zones: MasterDataZonesState.State;
    permitTypes: PermitTypeState.State;
    tariffs: TariffState.List.State;
}) {
    const { currentLogin, operatorData } = useOperatorContext();
    const hasTariffs = tariffs.data.length > 0;
    const hasPermitTypes =
        permitTypes.data.filter(
            permitType => permitType.operatorState == 'ACTIVE',
        ).length > 0;
    const hasZones = zones.data.filter(z => !z.isFromForeignMandant).length > 0;
    const currentYear = DateTime.now().year;
    const hasParkings = operatorData.hasBarrierGateZones;
    const hasPeripheryLog = operatorData.hasPeripheryHistoryZones;
    const hasRoute = useHasRoute();

    return (
        <div
            className={css({
                fontFamily: 'Roboto',
                fontSize: 14,
                display: 'flex',
                letterSpacing: '0.02em',
                lineHeight: '16px',
                fontWeight: 500,
            })}
        >
            <div>
                <NavbarDropdownApplicationItem
                    label={
                        <Localized
                            de="Allgemein"
                            fr="Général"
                            it="Generale"
                            en="General"
                        />
                    }
                />

                {currentLogin.permissions.masterDataAndStatistics && (
                    <StandardNavItem
                        onClose={onClose}
                        path={''}
                        route={OperatorAppRoutes.Home}
                    />
                )}
                {(hasZones || hasPermitTypes || hasParkings) && (
                    <StandardNavItem
                        onClose={onClose}
                        path={'activity-statement'}
                        route={OperatorAppRoutes.ActivityStatement}
                    />
                )}
                {hasTariffs && (
                    <StandardNavItem
                        onClose={onClose}
                        path={'tariffs'}
                        route={OperatorAppRoutes.Tariffs}
                    />
                )}
                {operatorData.hasQuotaConfigs && (
                    <StandardNavItem
                        onClose={onClose}
                        path={'quotas'}
                        route={OperatorAppRoutes.QuotasOverview}
                    />
                )}
                <StandardNavItem
                    onClose={onClose}
                    path={'logins'}
                    route={OperatorAppRoutes.Logins}
                />
                <StandardNavItem
                    onClose={onClose}
                    path={`check-in-statistics?YEAR=${currentYear}%2C${
                        currentYear - 1
                    }`}
                    route={OperatorAppRoutes.StatisticsCheckins}
                />
                <StandardNavItem
                    onClose={onClose}
                    path={`permit-statistics?YEAR=${currentYear}%2C${
                        currentYear - 1
                    }`}
                    route={OperatorAppRoutes.StatisticsPermits}
                />
            </div>

            {(hasRoute(OperatorAppRoutes.OnstreetTransactions) ||
                hasRoute(OperatorAppRoutes.EnforcementLog) ||
                hasRoute(OperatorAppRoutes.Zones) ||
                hasRoute(OperatorAppRoutes.UsbSticks) ||
                hasRoute(OperatorAppRoutes.ParkingMeters)) && (
                <div>
                    <NavbarDropdownApplicationItem
                        label={
                            <Localized
                                de="Onstreet"
                                fr="Onstreet"
                                en="Onstreet"
                                it="Onstreet"
                            />
                        }
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'onstreet-transactions'}
                        route={OperatorAppRoutes.OnstreetTransactions}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'enforcement-log'}
                        route={OperatorAppRoutes.EnforcementLog}
                    />
                    <HorizontalSeperator
                        routeType={OperatorRouteType.Onstreet}
                        hasRoute={hasRoute}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'zones'}
                        route={OperatorAppRoutes.Zones}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'parking-meters'}
                        route={OperatorAppRoutes.ParkingMeters}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'usb-sticks'}
                        route={OperatorAppRoutes.UsbSticks}
                    />
                </div>
            )}

            {(hasRoute(OperatorAppRoutes.OffstreetTransactions) ||
                hasRoute(OperatorAppRoutes.OffstreetGuestProducts) ||
                hasRoute(OperatorAppRoutes.OffstreetEvents) ||
                (hasRoute(OperatorAppRoutes.PeripheryHistory) &&
                    hasPeripheryLog) ||
                (hasRoute(OperatorAppRoutes.Parkings) && hasParkings) ||
                hasRoute(OperatorAppRoutes.CloudConnectorPeripheries) ||
                hasRoute(OperatorAppRoutes.CloudConnectorCounterOverview) ||
                hasRoute(OperatorAppRoutes.CloudConnectorRecodeStatements) ||
                hasRoute(OperatorAppRoutes.PayStationStatement)) && (
                <div>
                    <NavbarDropdownApplicationItem
                        label={
                            <Localized
                                de="Offstreet"
                                fr="Offstreet"
                                en="Offstreet"
                                it="Offstreet"
                            />
                        }
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'offstreet-transactions'}
                        route={OperatorAppRoutes.OffstreetTransactions}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'guest-products'}
                        route={OperatorAppRoutes.OffstreetGuestProducts}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'offstreet-events'}
                        route={OperatorAppRoutes.OffstreetEvents}
                    />
                    {hasPeripheryLog && (
                        <StandardNavItem
                            onClose={onClose}
                            path={'device-history'}
                            route={OperatorAppRoutes.PeripheryHistory}
                        />
                    )}
                    <HorizontalSeperator
                        routeType={OperatorRouteType.Offstreet}
                        hasParkings={hasParkings}
                        hasRoute={hasRoute}
                    />
                    {hasParkings && (
                        <StandardNavItem
                            onClose={onClose}
                            path={'parkings/list'}
                            route={OperatorAppRoutes.ParkingsList}
                        />
                    )}
                    <StandardNavItem
                        onClose={onClose}
                        path={'offstreet-devices'}
                        route={OperatorAppRoutes.CloudConnectorPeripheries}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'counters'}
                        route={OperatorAppRoutes.CloudConnectorCounterOverview}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'recode/statement'}
                        route={OperatorAppRoutes.CloudConnectorRecodeStatements}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'pay-station-statement'}
                        route={OperatorAppRoutes.PayStationStatement}
                    />
                </div>
            )}

            {(hasRoute(OperatorAppRoutes.ClearancesPermitsList) ||
                hasRoute(OperatorAppRoutes.ClearanceRequests) ||
                hasRoute(OperatorAppRoutes.CustomerAccounts) ||
                hasRoute(OperatorAppRoutes.SpecialReports) ||
                (hasRoute(OperatorAppRoutes.PermitTypes) &&
                    hasPermitTypes)) && (
                <div>
                    <NavbarDropdownApplicationItem
                        label={
                            <Localized
                                de="Parkingpay"
                                fr="Parkingpay"
                                en="Parkingpay"
                                it="Parkingpay"
                            />
                        }
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'clearance-permit-list'}
                        route={OperatorAppRoutes.ClearancesPermitsList}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'clearance-requests'}
                        route={OperatorAppRoutes.ClearanceRequests}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'customer-accounts'}
                        route={OperatorAppRoutes.CustomerAccounts}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'special-reports'}
                        route={OperatorAppRoutes.SpecialReports}
                    />
                    <HorizontalSeperator
                        routeType={OperatorRouteType.ParkingPay}
                        hasPermitTypes={hasPermitTypes}
                        hasRoute={hasRoute}
                    />
                    {hasPermitTypes && (
                        <StandardNavItem
                            onClose={onClose}
                            path={'permit-types'}
                            route={OperatorAppRoutes.PermitTypes}
                        />
                    )}
                </div>
            )}

            {(hasRoute(OperatorAppRoutes.ParkingaboUsers) ||
                hasRoute(OperatorAppRoutes.ParkingaboProducts) ||
                hasRoute(OperatorAppRoutes.ParkingaboProductTypes)) && (
                <div>
                    <NavbarDropdownApplicationItem
                        label={
                            <Localized
                                de="Parkingabo"
                                fr="Parkingabo"
                                it="Parkingabo"
                                en="Parkingabo"
                            />
                        }
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={
                            'parkingabo/users?STATUS=ACTIVE%2CLOCKED%2CPENDING'
                        }
                        route={OperatorAppRoutes.ParkingaboUsers}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'parkingabo/products?STATUS=ACTIVE'}
                        route={OperatorAppRoutes.ParkingaboProducts}
                    />
                    <HorizontalSeperator
                        routeType={OperatorRouteType.Parkingabo}
                        hasRoute={hasRoute}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'parkingabo/roles'}
                        route={OperatorAppRoutes.ParkingaboRoles}
                    />
                    <StandardNavItem
                        onClose={onClose}
                        path={'parkingabo/product-types'}
                        route={OperatorAppRoutes.ParkingaboProductTypes}
                    />
                </div>
            )}

            {(operatorData.parkomaticSmartfolio ||
                hasRoute(OperatorAppRoutes.Parkmonitor) ||
                hasRoute(OperatorAppRoutes.MiOffice)) &&
                !operatorData.enforcementOnly && (
                    <div>
                        <NavbarDropdownApplicationItem
                            label={
                                <Localized
                                    de="Externe Module"
                                    fr="Modules externes"
                                    it="Moduli esterni"
                                    en="External Modules"
                                />
                            }
                        />
                        <StandardNavItem
                            onClose={onClose}
                            path={'parkmonitor'}
                            route={OperatorAppRoutes.Parkmonitor}
                        />
                        {operatorData.parkomaticSmartfolio && (
                            <NavbarDropdownItemForeignLink
                                label={
                                    <Localized
                                        de="Smartfolio"
                                        fr="Smartfolio"
                                        it="Smartfolio"
                                        en="Smartfolio"
                                    />
                                }
                                link={'https://my.parkfolio.com/'}
                            />
                        )}
                        <StandardNavItem
                            onClose={onClose}
                            path={'mi-office'}
                            route={OperatorAppRoutes.MiOffice}
                        />
                    </div>
                )}
        </div>
    );
}

function StandardNavItem({
    onClose,
    route,
    path,
}: {
    onClose: () => void;
    route: OperatorAppRoutes;
    path: string;
}) {
    const { operatorData } = useOperatorContext();

    return (
        <RestrictedComponent route={route}>
            <NavLink
                to={path}
                end={route === OperatorAppRoutes.Home}
                className={({ isActive }) =>
                    `${navLinkClass} ${isActive ? activeNavLinkClass : ''}`
                }
                onClick={onClose}
            >
                {getNavigationText(route, operatorData)}
            </NavLink>
        </RestrictedComponent>
    );
}

function HorizontalSeperator({
    routeType,
    hasParkings,
    hasPermitTypes,
    hasRoute,
}: {
    routeType: OperatorRouteType;
    hasParkings?: boolean;
    hasPermitTypes?: boolean;
    hasRoute: (route: OperatorAppRoutes) => boolean;
}): JSX.Element | null {
    function hasHorizontalSeperator(): boolean {
        switch (routeType) {
            case OperatorRouteType.Default:
            case OperatorRouteType.External:
                return false;
            case OperatorRouteType.Onstreet:
                return (
                    hasRoute(OperatorAppRoutes.Zones) ||
                    hasRoute(OperatorAppRoutes.ParkingMeters) ||
                    hasRoute(OperatorAppRoutes.UsbSticks)
                );
            case OperatorRouteType.Offstreet:
                return (
                    Boolean(hasParkings) && hasRoute(OperatorAppRoutes.Parkings)
                );
            case OperatorRouteType.ParkingPay:
                return (
                    Boolean(hasPermitTypes) &&
                    hasRoute(OperatorAppRoutes.PermitTypes)
                );
            case OperatorRouteType.Parkingabo:
                return (
                    hasRoute(OperatorAppRoutes.ParkingaboRoles) ||
                    hasRoute(OperatorAppRoutes.ParkingaboProductTypes)
                );
        }
    }

    if (hasHorizontalSeperator()) {
        return (
            <Box sx={{ padding: '0 14px' }}>
                <hr />
            </Box>
        );
    } else {
        return null;
    }
}

function useHasRoute() {
    const { operatorData, currentLogin } = useOperatorContext();
    return useCallback(
        (appRoute: OperatorAppRoutes) =>
            isRouteAllowed(operatorData, currentLogin, appRoute),
        [operatorData, currentLogin],
    );
}

function ChangeOperatorLogin() {
    const navigate = useNavigate();
    return (
        <IconButton
            onClick={() => {
                navigate('/select-operator');
            }}
            sx={theme => ({
                color: theme.palette.primary.contrastText,
                ...Typo.robotoRegular,
                ':hover': {
                    color: theme.palette.blue.electric,
                },
            })}
        >
            <Edit />
        </IconButton>
    );
}
